import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  Divider, 
  Chip, 
  Paper 
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { sportNewsApi, sportNewsCategoryApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { fDate1 } from "../../utils/formatTime";
import { capitalize, TrimText } from "../../utils/helpers";
import { IMAGE_BASE } from "../../utils/constants";
import { Loader } from "../../components/CustomLoader";
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function ViewSportNews() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("N/A");
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (id) {
      setLoading(true);
      sportNewsApi.getOneSportNews(id)
        .then(async (res) => {
          const news = res?.data?.data;
          setData(news);
          setImageError(false);
          if (news?.categoryId) {
            try {
              const catRes = await sportNewsCategoryApi.getOneSportNewsCategory(
                news.categoryId
              );
              setCategoryName(catRes?.data?.data?.name || "N/A");
            } catch {
              setCategoryName("N/A");
            }
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  if (loading) {
    return <Loader />;
  }

  if (!data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" color="error" align="center">
            Sport News not found
          </Typography>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button variant="contained" onClick={() => navigate("/sport-news")}>
              Back to List
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Helmet>
        <title>View Sport News | {data?.title}</title>
      </Helmet>
      
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">View Sport News</Typography>
          <Button variant="outlined" onClick={() => navigate("/sport-news")}>
            Back
          </Button>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
              {data.image && !imageError ? (
                <img 
                  src={`${IMAGE_BASE}${data.image.replace(/\\/g, '/')}`}
                  alt={data.title}
                  style={{ width: "100%", maxHeight: "300px", objectFit: "cover", borderRadius: "8px" }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <Box sx={{ width: "100%", height: "200px", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px" }}>
                  <Typography color="textSecondary">No Image Available</Typography>
                </Box>
              )}
              
              <Box mt={3} display="flex" flexDirection="column" gap={1.5} textAlign="left">
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" color="textSecondary">Status:</Typography>
                  <Chip 
                    label={data.status === 1 ? "Active" : "Inactive"} 
                    color={data.status === 1 ? "success" : "default"} 
                    size="small" 
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" color="textSecondary">Trending:</Typography>
                  <Chip 
                    label={data.featured ? "Yes" : "No"} 
                    color={data.featured ? "primary" : "default"} 
                    size="small" 
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" color="textSecondary">Views:</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <VisibilityIcon fontSize="small" color="action" />
                    <Typography variant="body2">{data.views || 0}</Typography>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" color="textSecondary">Created At:</Typography>
                  <Typography variant="body2">{fDate1(data.createdAt)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle2" color="textSecondary">Last Modified:</Typography>
                  <Typography variant="body2">{fDate1(data.modifiedAt)}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box mb={3}>
              <Typography variant="h3" gutterBottom>
                {data.title}
              </Typography>
              
              <Box display="flex" gap={2} mb={2}>
                <Chip label={`Category: ${capitalize(categoryName)}`} color="secondary" variant="outlined" />
                <Chip label={`Author: ${capitalize(data.author || 'N/A')}`} variant="outlined" />
              </Box>
              
              {data.tags && data.tags.length > 0 && (
                <Box display="flex" gap={1} mb={3} flexWrap="wrap">
                  {data.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box mb={3}>
              <Typography variant="h6" gutterBottom color="primary">
                Summary
              </Typography>
              <Typography variant="body1" sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, borderRadius: 1 }}>
                {data.shortDescription}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="h6" gutterBottom color="primary">
                Article
              </Typography>
              <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <Box
                  className="sport-news-html-content"
                  dangerouslySetInnerHTML={{ __html: data.description }}
                  sx={{
                    wordBreak: "break-word",
                    overflow: "hidden", // clearfix for floated images
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "8px",
                    },
                    // Default: the editor's "in line with text" image has no
                    // extra class at all and should flow inside the sentence,
                    // not be forced onto its own block line.
                    "& img:not(.float-left):not(.float-right):not(.align-center)": {
                      verticalAlign: "middle",
                      margin: "0 4px",
                    },
                    "& img.float-left": {
                      float: "left",
                      margin: "4px 16px 12px 0",
                    },
                    "& img.float-right": {
                      float: "right",
                      margin: "4px 0 12px 16px",
                    },
                    "& img.align-center": {
                      display: "block",
                      margin: "16px auto",
                    },
                  }}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
